
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Typography,
} from "@mui/material";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";

// =====================================================
// PRICING PLANS
// =====================================================

export const plans = [
  {
    name: "Free",
    price: "0",
    guests: "Up to 10 guests",
    description:
      "Perfect for exploring Elevate before your event. Share a QR code and collect guest photos into one gallery.",
    cta: "Start Free",
    to: "/signup",
    highlight: false,
  },
  {
    name: "Holiday Special",
    price: "34.99",
    guests: "Up to 100 guests",
    description:
      "Limited-time offer with unlimited photos and Boomerangs for up to 100 guests.",
    cta: "Create an Event",
    to: "/signup",
    highlight: false,
    holidayOffer: true,
  },
  {
    name: "VIP Experience",
    price: "49.99",
    guests: "Up to 150 guests",
    description:
      "For up to 150 guests – excellent for weddings, graduations, engagements, and more.",
    cta: "Create an Event",
    to: "/signup",
    highlight: true,
    badge: "Most Popular",
  },
  {
    name: "Royal Affair",
    price: "99.99",
    guests: "Up to 300 guests",
    description:
      "For 150–300 guests – ideal for large weddings, corporate events, or planners.",
    cta: "Create an Event",
    to: "/signup",
    highlight: false,
  },
];

// =====================================================
// GUEST COUNT TIERS
// =====================================================

const TIERS = [
  {
    max: 10,
    label: "Best for 1–10 guests",
    planName: "Free",
  },
  {
    max: 100,
    label: "Best for 10–100 guests",
    planName: "Holiday Special",
  },
  {
    max: 150,
    label: "Best for 100–150 guests",
    planName: "VIP Experience",
  },
  {
    max: 300,
    label: "Best for 150–300 guests",
    planName: "Royal Affair",
  },
];

const getPlanByName = (name) =>
  plans.find((p) => p.name === name) || plans[0];

// =====================================================
// PRICING GUEST COUNT SECTION
// IMPORTANT: NAMED EXPORT FOR HOME.JS
// =====================================================

export const PricingGuestCountSection = ({
  title = "Choose your guest count",
  subtitle = "Select the range that matches your event.",
  defaultIndex = 0,
  sx = {},
}) => {
  const [guestIdx, setGuestIdx] = useState(defaultIndex);

  const selectedTier = TIERS[guestIdx] || TIERS[0];

  const selected = useMemo(
    () => getPlanByName(selectedTier.planName),
    [selectedTier]
  );

  const isFree = selected.name === "Free";
  const isHoliday = selected.name === "Holiday Special";

  const photoText = isFree
    ? "25 photos per guest"
    : "Unlimited photo uploads";

  const boomerangText = isFree
    ? "Photos only"
    : "Unlimited Boomerangs";

  const coreFeatures = [
    "Your custom QR code + link",
    "No app or guest sign-in",
    "Instant photo uploads",
    "Memories stay saved after your event",
  ];

  return (
    <Box sx={{ py: { xs: 5, md: 7 }, ...sx }}>
      <Container maxWidth="lg">
        {title && (
          <Typography
            component="h2"
            sx={{
              fontWeight: 900,
              fontSize: {
                xs: "1.42rem",
                sm: "1.75rem",
                md: "2rem",
              },
              letterSpacing: "-0.02em",
              lineHeight: 1.12,
              mb: 1.25,
              maxWidth: { xs: 360, sm: "none" },
            }}
          >
            {title}
          </Typography>
        )}

        {subtitle && (
          <Typography
            sx={{
              color: "#4B5563",
              fontWeight: 700,
              mb: 3,
            }}
          >
            {subtitle}
          </Typography>
        )}

        <Box
          sx={{
            border: "1px solid rgba(0,0,0,0.10)",
            borderRadius: 4,
            p: { xs: 2, md: 3 },
            background: "#fff",
            boxShadow: "0 10px 34px rgba(0,0,0,0.06)",
          }}
        >
          {/* GUEST COUNT SELECTOR */}

          <Box
            role="group"
            aria-label="Guest count selector"
            sx={{
              mt: 1,
              mx: { xs: 0.5, sm: 1.5 },
              background: "#F3F4F6",
              borderRadius: 10,
              p: 0.8,
              border: "1px solid rgba(24,119,242,0.35)",
              touchAction: "pan-y",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${TIERS.length}, 1fr)`,
                gap: 0,
                overflow: "hidden",
                borderRadius: 10,
              }}
            >
              {TIERS.map((t, idx) => {
                const isOn = idx <= guestIdx;

                return (
                  <Box
                    key={t.max}
                    role="button"
                    tabIndex={0}
                    aria-label={`Select up to ${t.max} guests`}
                    aria-pressed={idx === guestIdx}
                    onClick={() => setGuestIdx(idx)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setGuestIdx(idx);
                      }
                    }}
                    sx={{
                      height: { xs: 44, sm: 46 },
                      cursor: "pointer",
                      userSelect: "none",
                      touchAction: "manipulation",
                      borderRadius: 4,
                      background: isOn
                        ? "#3B82F6"
                        : "#E5E7EB",
                      borderRight:
                        idx < TIERS.length - 1
                          ? "2px solid rgba(24,119,242,0.85)"
                          : "none",
                      boxShadow: isOn
                        ? "inset 0 0 0 1px rgba(255,255,255,0.10)"
                        : "inset 0 0 0 1px rgba(255,255,255,0.06)",
                      outline:
                        idx === guestIdx
                          ? "2px solid rgba(255,255,255,0.20)"
                          : "none",
                      outlineOffset: "-2px",
                      "&:active": {
                        transform: "scale(0.99)",
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {/* GUEST COUNT LABELS */}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${TIERS.length}, 1fr)`,
              alignItems: "center",
              justifyItems: "center",
              gap: { xs: 0.5, sm: 0.75 },
              mt: 0.75,
              mb: 1.5,
            }}
          >
            {TIERS.map((t, idx) => (
              <Typography
                key={t.max}
                component="span"
                sx={{
                  fontSize: { xs: 12, sm: 13 },
                  fontWeight: idx === guestIdx ? 800 : 700,
                  color:
                    idx === guestIdx
                      ? "#111827"
                      : "#6B7280",
                  lineHeight: 1.1,
                  userSelect: "none",
                }}
              >
                {t.max}
              </Typography>
            ))}
          </Box>

          {/* SELECTED GUEST RANGE */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 0.7,
                borderRadius: 999,
                background: "#EEF2FF",
                color: "#1F2937",
                border: "1px solid rgba(0,0,0,0.06)",
                fontSize: 14,
                fontWeight: 800,
                lineHeight: 1,
                textAlign: "center",
              }}
            >
              {selectedTier.label}
            </Box>
          </Box>

          {/* VIP UPSELL */}

          {isHoliday && (
            <Button
              onClick={() => setGuestIdx(2)}
              sx={{
                display: "flex",
                mx: "auto",
                mt: 0.5,
                mb: 2,
                px: 1.5,
                py: 0.75,
                textTransform: "none",
                fontWeight: 800,
                fontSize: {
                  xs: "0.85rem",
                  sm: "0.95rem",
                },
                color: "#1877F2",
                textAlign: "center",
                lineHeight: 1.4,
                borderRadius: 2,
                "&:hover": {
                  backgroundColor: "rgba(24,119,242,0.06)",
                },
              }}
            >
              Need more than 100 guests? Go VIP →
            </Button>
          )}

          {/* PRICING CARD */}

          <Box
            sx={{
              mt: 2,
              borderRadius: 4,
              p: { xs: 2.25, md: 3 },
              background: selected.highlight
                ? "#0B0B0B"
                : "#F9FAFB",
              color: selected.highlight ? "#fff" : "#000",
              border: selected.highlight
                ? "1px solid rgba(255,255,255,0.14)"
                : "1px solid rgba(0,0,0,0.10)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {selected.badge && (
              <Chip
                label={selected.badge}
                sx={{
                  position: "absolute",
                  top: 14,
                  right: 14,
                  fontWeight: 900,
                  borderRadius: 999,
                  background: "#fff",
                  color: "#000",
                }}
              />
            )}

            {/* PLAN NAME, PRICE, CAPACITY */}

            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1.5,
                pr: selected.badge ? { xs: 0, sm: 15 } : 0,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: "1.25rem",
                  }}
                >
                  {selected.name}
                </Typography>

                {/* LIMITED TIME HOLIDAY OFFER */}

                {isHoliday && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.6,
                      mt: 0.6,
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: "#15803D",
                        flexShrink: 0,
                      }}
                    />

                    <Typography
                      sx={{
                        fontSize: { xs: 11, sm: 12 },
                        fontWeight: 800,
                        color: "#15803D",
                        letterSpacing: "-0.01em",
                      }}
                    >
                      Limited-time offer · Ending soon
                    </Typography>
                  </Box>
                )}

                <Typography
                  sx={{
                    mt: 0.75,
                    fontWeight: 900,
                    fontSize: {
                      xs: "2.1rem",
                      md: "2.4rem",
                    },
                    letterSpacing: "-0.01em",
                    lineHeight: 1.05,
                  }}
                >
                  {selected.price === "0"
                    ? "$0"
                    : `$${selected.price}`}
                </Typography>

                {!isFree && (
                  <Typography
                    sx={{
                      mt: 0.5,
                      fontSize: 12,
                      fontWeight: 600,
                      color: selected.highlight
                        ? "rgba(255,255,255,0.7)"
                        : "#6B7280",
                    }}
                  >
                    One-time payment
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  mt: selected.badge
                    ? { xs: 4.5, sm: 0 }
                    : 0,
                  px: 1.25,
                  py: 0.65,
                  borderRadius: 10,
                  background: selected.highlight
                    ? "rgba(255,255,255,0.13)"
                    : "#E9EDF3",
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 800,
                    color: selected.highlight
                      ? "#fff"
                      : "#374151",
                  }}
                >
                  {selected.guests}
                </Typography>
              </Box>
            </Box>

            <Divider
              sx={{
                my: 2,
                borderColor: selected.highlight
                  ? "rgba(255,255,255,0.12)"
                  : "rgba(0,0,0,0.08)",
              }}
            />

            {/* PHOTO AND BOOMERANG BENEFITS */}

            <Box
              sx={{
                display: "grid",
                rowGap: 1.25,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                }}
              >
                <CameraAltOutlinedIcon
                  sx={{
                    fontSize: 20,
                    color: selected.highlight
                      ? "#7DB7FF"
                      : "#6B7280",
                  }}
                />

                <Typography
                  sx={{
                    fontWeight: 700,
                    color: isHoliday
                      ? "#15803D"
                      : selected.highlight
                      ? "#fff"
                      : "#374151",
                  }}
                >
                  {photoText}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                }}
              >
                <AllInclusiveIcon
                  sx={{
                    fontSize: 20,
                    color: selected.highlight
                      ? "#7DB7FF"
                      : "#6B7280",
                  }}
                />

                <Typography
                  sx={{
                    fontWeight: 900,
                    color: isHoliday
                      ? "#15803D"
                      : isFree
                      ? "#6B7280"
                      : selected.highlight
                      ? "#7DB7FF"
                      : "#111827",
                  }}
                >
                  {boomerangText}
                </Typography>
              </Box>
            </Box>

            <Divider
              sx={{
                my: 2,
                borderColor: selected.highlight
                  ? "rgba(255,255,255,0.12)"
                  : "rgba(0,0,0,0.08)",
              }}
            />

            {/* CORE FEATURES */}

            <Box
              component="ul"
              sx={{
                listStyle: "none",
                p: 0,
                m: 0,
                display: "grid",
                rowGap: 0.75,
                maxWidth: 640,
              }}
            >
              {coreFeatures.map((feature) => (
                <Box
                  component="li"
                  key={feature}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CheckCircleRoundedIcon
                    sx={{
                      fontSize: 20,
                      color: selected.highlight
                        ? "#fff"
                        : "#111",
                      opacity: selected.highlight
                        ? 0.95
                        : 0.9,
                    }}
                  />

                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: selected.highlight
                        ? "rgba(255,255,255,0.90)"
                        : "#111",
                    }}
                  >
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* CREATE EVENT BUTTON */}

            <Button
              component={Link}
              to={selected.to}
              variant="contained"
              sx={{
                mt: 2.25,
                width: {
                  xs: "100%",
                  sm: "auto",
                },
                textTransform: "none",
                fontWeight: 900,
                borderRadius: 2.5,
                py: 1.15,
                px: 2.4,
                backgroundColor: selected.highlight
                  ? "#1877F2"
                  : "#111827",
                color: "#fff",
                "&:hover": {
                  backgroundColor: selected.highlight
                    ? "#1567d4"
                    : "#000",
                },
              }}
            >
              {selected.cta}
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

// =====================================================
// PRICING PAGE
// =====================================================

const Pricing = () => {
  useEffect(() => {
    document.title = "Pricing — Elevate";

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, []);

  return (
    <Box
      sx={{
        background: "#fff",
        color: "#000",
        width: "100%",
      }}
    >
      <Box
        sx={{
          pt: { xs: 3, md: 4 },
          pb: { xs: 1, md: 2 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 860 }}>
            <Typography
              component="h1"
              sx={{
                fontWeight: 900,
                letterSpacing: "-0.02em",
                lineHeight: 1.05,
                fontSize: {
                  xs: "clamp(2rem, 7.6vw, 2.8rem)",
                  md: "clamp(2.8rem, 4vw, 3.4rem)",
                },
              }}
            >
              How many guests are you expecting?
            </Typography>

            <Typography
              sx={{
                mt: 1.5,
                color: "#4B5563",
                maxWidth: 740,
                lineHeight: 1.6,
                fontSize: {
                  xs: "1.02rem",
                  md: "1.1rem",
                },
                fontWeight: 700,
              }}
            >
              Select the range that matches your event.
            </Typography>
          </Box>
        </Container>
      </Box>

      <PricingGuestCountSection
        title=""
        subtitle=""
        sx={{ pt: { xs: 2, md: 3 } }}
      />
    </Box>
  );
};

// =====================================================
// DEFAULT EXPORT
// =====================================================

export default Pricing;
